import { watch } from 'vue'
import { useApiConfig } from '~/config/api'
import { useBarcodingSettings } from '~/composables/useBarcodingSettings'
import { useAuthStore } from '~/stores/auth'
import {
  applyBarcodePrefixes, loadBarcodePrefixes, parseBarcodeSettings, saveBarcodePrefixes,
} from '~/utils/barcodePrefixes'
import { reach } from '~/utils/lookupReach'
import { parsePrinters, savePrinters } from '~/utils/printers'

/**
 * The barcode prefixes set up in BOB (task 2201): the tablet's last copy at once, so a tablet
 * started out of range reads labels as it did; then the server's, each time someone logs in.
 * No answer changes nothing.
 *
 * The site's other barcoding settings are read at the same moment (the offline limit, Mark
 * 2026-10-15): a tablet should know its limit before it ever loses the signal. So is the site's
 * printer list (task 2449, utils/printers.ts), kept for printing with the internet down.
 */
export default defineNuxtPlugin(() => {
  applyBarcodePrefixes(loadBarcodePrefixes())

  const auth = useAuthStore()

  async function readFromBob() {
    try {
      const apiConfig = useApiConfig()
      const response = await reach(`${apiConfig.baseURL}/barcoding-settings/barcodes`, {
        headers: { Accept: 'application/json' },
      })
      if (!response.ok) return
      const latest = parseBarcodeSettings(await response.json())
      applyBarcodePrefixes(latest)
      saveBarcodePrefixes(latest)
    } catch {
      // Out of range, or the server is busy: keep what the tablet has.
    }
  }

  async function readPrinters() {
    try {
      const apiConfig = useApiConfig()
      const response = await reach(`${apiConfig.baseURL}/printers`, { headers: { Accept: 'application/json' } })
      if (!response.ok) return
      savePrinters(parsePrinters(await response.json()))
    } catch {
      // Out of range: keep the list the tablet has.
    }
  }

  const barcoding = useBarcodingSettings()

  watch(() => auth.isAuthenticated, (now) => {
    if (!now) return
    void readFromBob()
    void readPrinters()
    void barcoding.refresh()
  }, { immediate: true })
})
